import React from "react";
import { Menu, ActionIcon, Select, Box, Text, Group, Divider } from "@mantine/core";
import { modals } from "@mantine/modals";
import {
    IconDotsVertical,
    IconUser,
    IconFlag,
    IconCalendar,
    IconCopy,
    IconTrash,
} from "@tabler/icons-react";
import { DatePickerInput } from "@mantine/dates";
import { createTask, updateTask, deleteTask, assignTask } from "../../services/workflowService";


const QuickActionsMenu = ({ task, users, onUpdate, opened, onOpenChange }) => {

    const handleToggle = (val) => {
        if (onOpenChange) onOpenChange(val);
    };

    const priorityOptions = [
        { value: "low", label: "🔵 Low" },
        { value: "medium", label: "🟡 Medium" },
        { value: "high", label: "🟠 High" },
        { value: "urgent", label: "🔴 Urgent" }
    ];

    const userOptions = users.map((user) => ({
        value: user.id.toString(),
        label: user.email
    }));

    const handlePriorityChange = async (priority) => {
        try {
            await updateTask(task.id, { priority });
            onUpdate();
        } catch (error) {
            console.error("Error updating priority:", error);
        }
    };

    const handleAssignChange = async (userId) => {
        try {
            await assignTask(task.id, userId ? parseInt(userId, 10) : null);
            onUpdate();
        } catch (error) {
            console.error("Error assigning task:", error);
        }
    };

    const handleDueDateChange = async (date) => {
        try {
            await updateTask(task.id, {
                dueDate: date ? new Date(date).toISOString() : null
            });
            onUpdate();
        } catch (error) {
            console.error("Error updating due date:", error);
        }
    };

    const handleDuplicate = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            const { title, description, priority, tags, columnId, assignedToId } = task;
            await createTask({
                title: `${title} (Copy)`,
                description,
                priority,
                tags: typeof tags === 'string' ? JSON.parse(tags) : tags,
                columnId,
                assignedToId
            });
            handleToggle(false);
            onUpdate();
        } catch (error) {
            console.error("Error duplicating task:", error);
        }
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        modals.openConfirmModal({
            title: 'Delete task',
            centered: true,
            children: (
                <Text size="sm">
                    Are you sure you want to delete this task? This action is irreversible.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                try {
                    await deleteTask(task.id);
                    handleToggle(false);
                    onUpdate();
                } catch (error) {
                    console.error("Error deleting task:", error);
                }
            },
        });
    };

    return (
        <Menu
            opened={opened}
            onChange={handleToggle}
            position="bottom-end"
            withArrow
            shadow="xl"
            withinPortal={true}
            closeOnItemClick={false}
            zIndex={6000}
            transitionProps={{ transition: 'pop', duration: 150 }}
        >
            <Menu.Target>
                <div
                    onPointerDown={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <ActionIcon
                        variant="subtle"
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleToggle(!opened);
                        }}
                    >
                        <IconDotsVertical size={16} />
                    </ActionIcon>
                </div>
            </Menu.Target>

            <Menu.Dropdown
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                p={4}
            >
                <Box px={10} py={6}>
                    <Text size="xs" fw={700} c="dimmed">Quick Actions</Text>
                </Box>

                <Box px={10} py={4}>
                    <Group gap="xs" wrap="nowrap">
                        <IconUser size={14} style={{ flexShrink: 0 }} />
                        <Select
                            placeholder="Assign to..."
                            data={userOptions}
                            value={task.assignedToId?.toString() || null}
                            onChange={handleAssignChange}
                            size="xs"
                            clearable
                            searchable
                            style={{ flex: 1 }}
                            comboboxProps={{ withinPortal: false }}
                        />
                    </Group>
                </Box>

                <Box px={10} py={4}>
                    <Group gap="xs" wrap="nowrap">
                        <IconFlag size={14} style={{ flexShrink: 0 }} />
                        <Select
                            placeholder="Priority"
                            data={priorityOptions}
                            value={task.priority}
                            onChange={handlePriorityChange}
                            size="xs"
                            style={{ flex: 1 }}
                            comboboxProps={{ withinPortal: false }}
                        />
                    </Group>
                </Box>

                <Box px={10} py={4}>
                    <Group gap="xs" wrap="nowrap">
                        <IconCalendar size={14} style={{ flexShrink: 0 }} />
                        <DatePickerInput
                            placeholder="Due date"
                            value={task.dueDate ? new Date(task.dueDate) : null}
                            onChange={handleDueDateChange}
                            size="xs"
                            clearable
                            style={{ flex: 1 }}
                            popoverProps={{ withinPortal: false }}
                        />
                    </Group>
                </Box>

                <Divider my={4} />

                <Menu.Item
                    leftSection={<IconCopy size={14} />}
                    onClick={handleDuplicate}
                >
                    <Text size="xs">Duplicate</Text>
                </Menu.Item>

                <Menu.Item
                    leftSection={<IconTrash size={14} />}
                    color="red"
                    onClick={handleDelete}
                >
                    <Text size="xs">Delete</Text>
                </Menu.Item>
            </Menu.Dropdown>
        </Menu>
    );
};

export default QuickActionsMenu;
